/** @file Topology plans: where each new vertex comes from, and per-vertex data rebuilt from those sources. */
import { normalizeVertexWeights } from '@pose2d/commands/mesh/normalizeVertexWeights';
import type { Id } from '@pose2d/format/types';

/** A vertex of the edited mesh: an old vertex, or an affine blend of old vertices (an inserted point). */
export type VertexSource = { kind: 'old'; v: number } | { kind: 'blend'; parts: readonly (readonly [vertex: number, weight: number])[] };

/** The edited mesh: one source per new vertex (new index order), triangles and edges in new indices, hull count. */
export interface TopologyPlan {
  sources: VertexSource[];
  triangles: number[];
  hull: number;
  /** null = the mesh keeps no editor edges. */
  edges: number[] | null;
}

type Influences = [Id, number][];

/** Rebuilds per-vertex data for a plan's sources. */
export const blendSources = {
  /** 2 numbers per new vertex (uvs, positions, deform offsets): copied, or the weighted sum of the parts. */
  pairs(values: readonly number[], sources: readonly VertexSource[]): number[] {
    const out: number[] = [];
    for (const s of sources) {
      const parts = s.kind === 'old' ? [[s.v, 1] as const] : s.parts;
      let x = 0;
      let y = 0;
      for (const [v, w] of parts) {
        x += w * (values[2 * v] ?? 0);
        y += w * (values[2 * v + 1] ?? 0);
      }
      out.push(x, y);
    }
    return out;
  },
  /** One influence list per new vertex: copied, or the parts' rows mixed by their non-negative shares and normalized. */
  rows(rows: readonly Influences[], sources: readonly VertexSource[]): Influences[] {
    return sources.map((s) => {
      if (s.kind === 'old') return (rows[s.v] ?? []).map(([b, w]): [Id, number] => [b, w]);
      const mixed = s.parts.flatMap(([v, w]) => (rows[v] ?? []).map(([b, x]): [Id, number] => [b, Math.max(0, w) * x]));
      return normalizeVertexWeights(mixed) ?? (rows[s.parts[0]?.[0] ?? 0] ?? []).map(([b, w]): [Id, number] => [b, w]);
    });
  },
  /** True when the plan keeps every old vertex in place (only triangles, hull or edges change). */
  isIdentity(sources: readonly VertexSource[], oldCount: number): boolean {
    return sources.length === oldCount && sources.every((s, i) => s.kind === 'old' && s.v === i);
  },
};
